import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Link as ScrollLink, scroller } from 'react-scroll';
import { Heart } from 'lucide-react';
import GitHubStarButton from '../common/GitHubStarButton';
import DownloadButton from '../common/DownloadButton';

const TwitterSvg = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const GithubSvg = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
  </svg>
);
const YoutubeSvg = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

interface LayoutProps {
  children: React.ReactNode;
}

const SiteLayout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLandingPage = location.pathname === '/';
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const id = 'ft-live-styles';
    const existingStyle = document.getElementById(id);
    const style = existingStyle || document.createElement('style');
    style.id = id;
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,700;1,400;1,700&family=Nunito:wght@400;500;600;700&family=Caveat:wght@500;600;700&display=swap');
      .ft-l-lora   { font-family: 'Lora', serif; }
      .ft-l-nunito { font-family: 'Nunito', sans-serif; }
      .ft-l-caveat { font-family: 'Caveat', cursive; }

      .vi-brand-button {
        align-items: center;
        background: transparent;
        background-image: none !important;
        border: 0;
        border-radius: 12px;
        color: var(--vi-text-primary);
        display: inline-flex;
        flex-shrink: 0;
        gap: 10px;
        padding: 5px 8px 5px 5px;
        text-decoration: none;
        transition: background 0.18s ease, color 0.18s ease;
      }

      .vi-header-row {
        align-items: center;
        display: grid;
        gap: clamp(12px, 2vw, 24px);
        grid-template-columns: minmax(150px, 1fr) auto minmax(150px, 1fr);
        height: 64px;
      }

      .vi-brand-button:hover,
      .vi-brand-button:focus-visible {
        background: rgba(255, 251, 240, 0.72);
        outline: none;
      }

      .vi-brand-button img {
        box-shadow: 0 1px 2px var(--vi-shadow-sm);
      }

      .vi-brand-text {
        color: var(--vi-text-primary);
        font-family: 'Lora', serif;
        font-size: 1.25rem;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 1;
        white-space: nowrap;
      }

      .vi-header-nav {
        align-items: center;
        display: flex;
        gap: clamp(18px, 2vw, 28px);
        justify-self: center;
        min-width: 0;
        padding: 0;
      }

      .vi-header-nav-link {
        background-image: none !important;
        color: var(--vi-text-secondary);
        cursor: pointer;
        display: inline-flex;
        font-family: 'Nunito', sans-serif;
        font-size: 15px;
        font-weight: 800;
        line-height: 1;
        padding: 7px 0 9px;
        position: relative;
        text-decoration: none;
        transition: color 0.18s ease;
        white-space: nowrap;
      }

      .vi-header-nav-link::after {
        background: var(--vi-accent-primary);
        border-radius: 999px;
        bottom: 0;
        content: "";
        height: 2px;
        left: 0;
        opacity: 0;
        position: absolute;
        right: 0;
        transform: scaleX(0.35);
        transform-origin: center;
        transition: opacity 0.18s ease, transform 0.18s ease;
      }

      .vi-header-nav-link:hover,
      .vi-header-nav-link:focus-visible {
        color: var(--vi-accent-primary);
        outline: none;
      }

      .vi-header-nav-link:hover::after,
      .vi-header-nav-link:focus-visible::after {
        opacity: 1;
        transform: scaleX(1);
      }

      .vi-header-actions {
        align-items: center;
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        justify-self: end;
        min-width: 0;
      }

      .vi-header-star,
      .vi-header-cta {
        flex: 0 0 auto;
      }

      .vi-header-star-button {
        height: 40px;
      }

      .vi-header-try-button {
        height: 40px;
        padding-bottom: 0;
        padding-top: 0;
      }

      .vi-menu-button {
        align-items: center;
        background: transparent;
        border: 0;
        border-radius: 14px;
        display: none;
        flex-direction: column;
        gap: 5px;
        height: 44px;
        justify-content: center;
        padding: 0;
        width: 44px;
      }

      .vi-menu-button:hover,
      .vi-menu-button:focus-visible {
        background: var(--vi-bg-surface-primary);
        outline: none;
      }

      .vi-menu-line {
        background: var(--vi-text-secondary);
        border-radius: 2px;
        display: block;
        height: 2px;
        transition: opacity 0.22s ease, transform 0.22s ease;
        width: 22px;
      }

      .vi-mobile-menu {
        background: rgba(255, 251, 240, 0.98);
        border-top: 1px solid var(--vi-border-secondary);
        box-shadow: 0 18px 40px var(--vi-shadow-warm-sm);
        padding: 16px 24px 24px;
      }

      .vi-mobile-nav {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .vi-mobile-nav-link {
        background-image: none !important;
        border-radius: 14px;
        color: var(--vi-text-secondary);
        cursor: pointer;
        display: block;
        font-family: 'Nunito', sans-serif;
        font-size: 17px;
        font-weight: 800;
        padding: 12px 14px;
        text-decoration: none;
      }

      .vi-mobile-nav-link:hover,
      .vi-mobile-nav-link:focus-visible {
        background: var(--vi-bg-surface-primary);
        color: var(--vi-text-primary);
        outline: none;
      }

      .vi-mobile-actions {
        align-items: center;
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-top: 16px;
      }

      .vi-mobile-actions > * {
        justify-content: center;
        min-height: 44px;
        width: min(220px, 100%);
      }

      .ft-l-footer {
        background: var(--vi-bg-page-alt);
        border-top: 1px solid var(--vi-border-secondary);
        color: var(--vi-text-primary);
      }

      .ft-l-footer-shell {
        padding-bottom: 0;
        padding-top: 0;
      }

      .ft-l-nav-grid {
        display: grid;
        gap: 34px 30px;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        padding: clamp(42px, 6vw, 58px) 0;
      }

      .ft-l-col-title {
        color: var(--vi-text-primary);
        font-family: 'Nunito', sans-serif;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: 0.12em;
        line-height: 1.2;
        margin-bottom: 15px;
        text-transform: uppercase;
      }

      .ft-l-link {
        color: var(--vi-text-body);
        display: inline-block;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.45;
        padding: 2px 0;
        text-decoration: none;
        transition: color 0.18s ease, transform 0.18s ease;
      }

      .ft-l-link-sub {
        margin-left: 14px;
      }

      .ft-l-link:hover {
        color: var(--vi-text-primary);
        transform: translateX(2px);
      }

      .ft-l-bottom {
        align-items: center;
        border-top: 1px solid var(--vi-border-secondary);
        display: flex;
        gap: 16px;
        justify-content: space-between;
        padding: 20px 0;
      }

      .ft-l-social-group {
        align-items: center;
        display: flex;
        gap: 20px;
      }

      .ft-l-social-icon {
        align-items: center;
        color: var(--vi-icon-secondary);
        display: flex;
        transition: color 0.2s ease;
      }

      .ft-l-social-icon:hover { color: var(--vi-accent-primary); }

      .ft-l-footer-meta {
        color: var(--vi-text-body);
        font-size: 13px;
        font-weight: 600;
      }

      .ft-l-signoff-link {
        color: var(--vi-accent-primary);
        text-decoration: none;
      }

      .ft-l-signoff-link:hover { color: var(--vi-text-primary); }

      @media (max-width: 1023px) {
        .vi-header-star {
          display: none;
        }
      }

      @media (max-width: 767px) {
        .vi-header-row {
          gap: 12px;
          grid-template-columns: minmax(0, 1fr) auto;
        }

        .vi-header-nav,
        .vi-header-cta {
          display: none;
        }

        .vi-menu-button {
          display: inline-flex;
        }

        .ft-l-nav-grid {
          gap: 34px 24px;
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .ft-l-bottom {
          align-items: flex-start;
          flex-direction: column;
        }
      }
    `;
    if (!existingStyle) document.head.appendChild(style);
    return () => {
      if (!existingStyle) document.getElementById(id)?.remove();
    };
  }, []);

  const navLinks = [
    { name: 'Features', to: 'features', path: '/features' },
    { name: 'Testimonials', to: 'wall-of-love', path: '/walloflove' },
    { name: 'Pricing', to: 'pricing', path: '/pricing' },
  ];

  const goHome = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setMenuOpen(false);

    if (isLandingPage) {
      event.preventDefault();
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  const isActiveNavLink = (path: string) => location.pathname === path;

  const handleTryFreeClick = () => {
    setMenuOpen(false);

    if (isLandingPage) {
      scroller.scrollTo('pricing', {
        smooth: true,
        duration: 500,
        offset: -64,
      });
      return;
    }

    navigate('/pricing');
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--vi-bg-page)' }}>

      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          backgroundColor: 'rgba(255, 251, 240, 0.94)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid var(--vi-border-primary)',
          boxShadow: '0 1px 0 var(--vi-border-secondary)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="h-16 flex items-center justify-between vi-header-row">

            {/* Logo */}
            <Link
              to="/"
              className="vi-brand-button"
              onClick={goHome}
              aria-label="Go to VoiceInk home"
            >
              <img
                src="/favicon.png"
                alt="VoiceInk Logo"
                className="w-8 h-8 rounded-lg"
                width={32}
                height={32}
              />
              <span className="vi-brand-text">VoiceInk</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex vi-header-nav" aria-label="Primary navigation">
              {navLinks.map((link) => (
                isLandingPage ? (
                  <ScrollLink
                    key={link.name}
                    to={link.to}
                    smooth={true}
                    duration={500}
                    offset={-64}
                    className="vi-header-nav-link"
                  >
                    {link.name}
                  </ScrollLink>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className="vi-header-nav-link"
                    aria-current={isActiveNavLink(link.path) ? 'page' : undefined}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </nav>

            {/* Right-side actions */}
            <div className="flex items-center justify-end gap-3 vi-header-actions">
              <div className="hidden lg:block vi-header-star">
                <GitHubStarButton repo="Beingpax/VoiceInk" className="vi-header-star-button" />
              </div>
              <div className="hidden md:block vi-header-cta">
                <DownloadButton
                  size="small"
                  className="vi-header-try-button"
                  onClick={handleTryFreeClick}
                >
                  Try Free
                </DownloadButton>
              </div>

              {/* Hamburger — mobile only, landing page only */}
              {isLandingPage && (
                <button
                  className="md:hidden flex flex-col items-center justify-center gap-[5px] h-11 w-11 vi-menu-button"
                  onClick={() => setMenuOpen(o => !o)}
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                >
                  <span
                    className="vi-menu-line"
                    style={{ transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none' }}
                  />
                  <span
                    className="vi-menu-line"
                    style={{ opacity: menuOpen ? 0 : 1 }}
                  />
                  <span
                    className="vi-menu-line"
                    style={{ transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none' }}
                  />
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Mobile menu drawer */}
        {isLandingPage && menuOpen && (
          <div className="md:hidden vi-mobile-menu">
            <nav className="vi-mobile-nav">
              {navLinks.map(link => (
                <ScrollLink
                  key={link.name}
                  to={link.to}
                  smooth={true}
                  duration={500}
                  offset={-64}
                  className="vi-mobile-nav-link"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.name}
                </ScrollLink>
              ))}
            </nav>
            <div className="vi-mobile-actions">
              <GitHubStarButton repo="Beingpax/VoiceInk" />
              <DownloadButton
                size="small"
                className="vi-header-try-button"
                onClick={handleTryFreeClick}
              >
                Try Free
              </DownloadButton>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="relative pt-16 flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="ft-l-footer ft-l-nunito">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ft-l-footer-shell">
          {/* Link columns */}
          <div className="ft-l-nav-grid">

            <div>
              <div className="ft-l-col-title">
                Product
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: 'Features', to: '/features' },
                  { label: 'Pricing', to: '/pricing' },
                  { label: 'iOS', to: '/ios' },
                  { label: 'Wall of Love', to: '/walloflove' },
                ].map(item => (
                  <li key={item.label}>
                    <Link to={item.to} className="ft-l-link ft-l-nunito">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="ft-l-col-title">
                Resources
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: 'Docs', to: '/docs' },
                  { label: 'Getting Started', to: '/docs/introduction' },
                  { label: 'Power Mode', to: '/docs/power-mode' },
                  { label: 'Recommended Models', to: '/docs/recommended-models' },
                  { label: 'Best dictation apps', to: '/best-dictation-apps' },
                ].map(item => (
                  <li key={item.label}>
                    <Link to={item.to} className="ft-l-link ft-l-nunito">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="ft-l-col-title">
                Support
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <li><Link to="/faq" className="ft-l-link ft-l-nunito">FAQ</Link></li>
                <li><Link to="/docs/common-issues" className="ft-l-link ft-l-nunito">Common Issues</Link></li>
                <li><Link to="/docs/license-management" className="ft-l-link ft-l-nunito">License Management</Link></li>
                <li><Link to="/contact" className="ft-l-link ft-l-nunito">Contact Pax</Link></li>
              </ul>
            </div>

            <div>
              <div className="ft-l-col-title">
                Download
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <li><Link to="/buy" className="ft-l-link ft-l-nunito">Buy VoiceInk</Link></li>
                <li><a href="https://github.com/Beingpax/VoiceInk/releases" target="_blank" rel="noopener noreferrer" className="ft-l-link ft-l-nunito">Download for Mac</a></li>
                <li><Link to="/ios" className="ft-l-link ft-l-nunito">Download for iOS</Link></li>
                <li><a href="https://github.com/Beingpax/VoiceInk/releases" target="_blank" rel="noopener noreferrer" className="ft-l-link ft-l-nunito">Changelog</a></li>
              </ul>
            </div>

            <div>
              <div className="ft-l-col-title">
                Legal
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <li><Link to="/privacy" className="ft-l-link ft-l-nunito">Privacy</Link></li>
                <li><Link to="/terms" className="ft-l-link ft-l-nunito">Terms</Link></li>
                <li><Link to="/affiliate" className="ft-l-link ft-l-nunito">Affiliate</Link></li>
                <li><a href="https://github.com/Beingpax/VoiceInk" target="_blank" rel="noopener noreferrer" className="ft-l-link ft-l-nunito">Source Code</a></li>
              </ul>
            </div>

          </div>

          {/* Bottom bar */}
          <div className="ft-l-bottom">
            <div className="ft-l-social-group">
              <a href="https://x.com/tryvoiceink" target="_blank" rel="noopener noreferrer" aria-label="X / Twitter" className="ft-l-social-icon">
                <TwitterSvg className="w-[17px] h-[17px]" />
              </a>
              <a href="https://github.com/Beingpax/VoiceInk" target="_blank" rel="noopener noreferrer" aria-label="GitHub" className="ft-l-social-icon">
                <GithubSvg className="w-[17px] h-[17px]" />
              </a>
              <a href="https://youtube.com/@tryvoiceink" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="ft-l-social-icon">
                <YoutubeSvg className="w-[17px] h-[17px]" />
              </a>
            </div>
            <div className="ft-l-footer-meta">
              Made with <Heart className="inline-block h-3.5 w-3.5 fill-current align-[-2px] text-[var(--vi-accent-primary)]" aria-label="love" /> by{' '}
              <a href="https://x.com/JoshiPax" target="_blank" rel="noopener noreferrer" className="ft-l-signoff-link">Pax</a>
              <span style={{ marginLeft: 14 }}>© 2026 VoiceInk</span>
            </div>
          </div>
        </div>

      </footer>

    </div>
  );
};

export default SiteLayout;
